import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PatientDashboard from './pages/PatientDashboard';
import DoctorDashboard from './pages/DoctorDashboard';
import DoctorProfile from './pages/DoctorProfile';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentRequired from './pages/PaymentRequired';
import AdminDashboard from './pages/AdminDashboard';
import FeedbackPage from './pages/FeedbackPage';
import ProfilePortal from './pages/ProfilePortal';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

// Inner component that has access to useNavigate
function AppRoutes({ user, login, logout, loading }) {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  const handleLogout = useCallback(() => {
    console.log('Logout called - clearing token and redirecting...');
    setIsLoggingOut(true);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    // Navigate first, then clear user state
    navigate('/', { replace: true });
    // Small delay to ensure navigation happens before state update
    setTimeout(() => {
      logout();
      setIsLoggingOut(false);
    }, 100);
  }, [logout, navigate]);

  if (loading || isLoggingOut) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout: handleLogout }}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={user ? <Navigate to={user.role === 'doctor' ? '/doctor-dashboard' : user.role === 'admin' ? '/admin-dashboard' : '/patient-dashboard'} /> : <LoginPage />} />
        <Route path="/register" element={user ? <Navigate to={user.role === 'doctor' ? '/doctor-dashboard' : user.role === 'admin' ? '/admin-dashboard' : '/patient-dashboard'} /> : <RegisterPage />} />
        <Route path="/patient-dashboard" element={user && user.role === 'patient' ? <PatientDashboard /> : <Navigate to="/login" />} />
        <Route path="/doctor-dashboard" element={user && user.role === 'doctor' ? <DoctorDashboard /> : <Navigate to="/login" />} />
        <Route path="/admin-dashboard" element={user && user.role === 'admin' ? <AdminDashboard /> : <Navigate to="/login" />} />
        <Route path="/doctor/:doctorId" element={<DoctorProfile />} />
        <Route path="/payment-success" element={user && user.role === 'doctor' ? <PaymentSuccess /> : <Navigate to="/login" />} />
        <Route path="/payment-required" element={user && user.role === 'doctor' ? <PaymentRequired /> : <Navigate to="/login" />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/profile" element={user ? <ProfilePortal /> : <Navigate to="/login" />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Routes>
    </AuthContext.Provider>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
  };

  return (
    <BrowserRouter>
      <AppRoutes user={user} login={login} logout={logout} loading={loading} />
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;